// Runs the two browser-side WebAuthn ceremonies: createPasskeyCredential
// (registration) and getPasskeyAssertion (sign-in). Each calls
// navigator.credentials.create()/.get(), converting server options in and
// the resulting credential out via ./webauthn, and turns a user-dismissed
// prompt or an unsupported browser into PasskeyCancelledError /
// PasskeyUnsupportedError instead of a raw DOMException. Callers still
// fetch the server options and POST the result themselves.
import {
  creationOptionsFromServer,
  requestOptionsFromServer,
  credentialToJSON,
  isPasskeySupported,
} from "./webauthn";
import PasskeyUnsupportedError from "./passkeyErrors";
import PasskeyCancelledError from "./passkeyCancelledError";

function wrapNavigatorError(error) {
  // Thrown for a user-dismissed prompt, a timeout, or an aborted (conditional
  // UI superseded) request alike -- none of those are real failures worth
  // surfacing as an error banner.
  if (error.name === "NotAllowedError" || error.name === "AbortError") {
    return new PasskeyCancelledError(error.message);
  }
  return error;
}

// serverOptions: the JSON body from POST /api/webauthn_credentials/registration_options
// (a WebAuthn::PublicKeyCredential::CreationOptions#as_json).
// Returns a JSON-serializable credential ready to POST to
// /api/webauthn_credentials.
export async function createPasskeyCredential(serverOptions, { signal } = {}) {
  if (!isPasskeySupported()) {
    throw new PasskeyUnsupportedError(
      "Passkeys are not supported in this browser."
    );
  }

  try {
    const credential = await navigator.credentials.create({
      publicKey: creationOptionsFromServer(serverOptions),
      signal,
    });
    return credentialToJSON(credential);
  } catch (e) {
    throw wrapNavigatorError(e);
  }
}

// serverOptions: the JSON body from POST /users/webauthn_authentication/options
// (a WebAuthn::PublicKeyCredential::RequestOptions#as_json).
// `mediation: "conditional"` drives the email-field autofill UX instead of a
// full-screen browser prompt; resolves to null if superseded/aborted.
export async function getPasskeyAssertion(
  serverOptions,
  { signal, mediation } = {}
) {
  if (!isPasskeySupported()) {
    throw new PasskeyUnsupportedError(
      "Passkeys are not supported in this browser."
    );
  }

  try {
    const credential = await navigator.credentials.get({
      publicKey: requestOptionsFromServer(serverOptions),
      signal,
      ...(mediation ? { mediation } : {}),
    });
    return credential ? credentialToJSON(credential) : null;
  } catch (e) {
    throw wrapNavigatorError(e);
  }
}
